import { DocumentActions } from '@/components/DocumentActions'
import AddIcon from '@mui/icons-material/Add'
import UndoIcon from '@mui/icons-material/Undo'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { SendDocumentIcon } from '@/components/SendDocument'
import EditIcon from '@mui/icons-material/Edit'
import { useOutlets, useOutstandingInvoices, usePayments, useReversePayment } from '@/api/hooks'
import { CustomerPicker } from '@/components/CustomerPicker'
import { RecordPaymentDialog } from '@/pages/billing/RecordPaymentDialog'
import { RefundsTab } from '@/pages/billing/RefundsTab'
import { WriteOffsTab } from '@/pages/billing/WriteOffsTab'
import { useAuthStore } from '@/auth/authStore'
import type { CustomerSummary, Payment } from '@/api/types'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatMoney,
  humanise,
  isoDate,
  PageHeader,
  StatusChip,
} from '@/components/common'

const MODES = ['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'GATEWAY', 'CHEQUE', 'CREDIT_ACCOUNT', 'ADJUSTMENT']

/**
 * What a receipt can be in. A cheque sits pending until it clears; a receipt
 * that spent its number but did not make it all the way through sits failed,
 * for the office to correct or reverse rather than leave as a gap.
 */
const STATUSES = ['RECEIVED', 'PENDING', 'FAILED', 'REVERSED']

export function PaymentsPage() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const [tab, setTab] = useState(0)
  const [mode, setMode] = useState('')
  const [customer, setCustomer] = useState<CustomerSummary | null>(null)
  const [outletId, setOutletId] = useState('')
  const [status, setStatus] = useState('')
  const { data: outlets } = useOutlets()
  // Opens on today. Every list and every report starts on the day being
  // worked, and the range is widened when somebody wants history.
  const [from, setFrom] = useState(() => isoDate(0))
  const [to, setTo] = useState(() => isoDate(0))
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [entryOpen, setEntryOpen] = useState(false)
  const [collecting, setCollecting] = useState(false)
  const [reversing, setReversing] = useState<Payment | null>(null)
  const [editing, setEditing] = useState<Payment | null>(null)

  const { data, isLoading, error } = usePayments({
    mode: mode || undefined,
    customerId: customer?.id,
    outletId: outletId || undefined,
    status: status || undefined,
    from,
    to,
    page,
    size,
  })

  // What is still owed, read without asking for it. The receipts list answers
  // what came in; the question behind it at a counter is always what has not,
  // and that used to mean leaving the screen for the ageing report.
  const { data: owed } = useOutstandingInvoices({
    customerId: customer?.id,
    outletId: outletId || undefined,
  })
  const bills = owed ?? []
  const owedTotal = bills.reduce((sum, bill) => sum + bill.balanceAmount, 0)

  return (
    <>
      <PageHeader
        title="Receipts"
        subtitle="Money taken and what it was put against, audited reversals, and money owed back"
        actions={
          canWrite('payment') && tab === 0 ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEntryOpen(true)}>
              Record a receipt
            </Button>
          ) : undefined
        }
      />

      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Receipts" />
        <Tab label="Refunds" />
        <Tab label="Write-offs" />
      </Tabs>

      {tab === 1 ? <RefundsTab /> : null}
      {tab === 2 ? <WriteOffsTab /> : null}
      {tab !== 0 ? null : (
      <>
      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            {/* Whose money it was. The first thing anybody asks a receipts
                list, and the one thing it could not be asked. */}
            <Box sx={{ minWidth: 260, flexGrow: 1 }}>
              <CustomerPicker
                value={customer}
                onChange={(chosen) => {
                  setCustomer(chosen)
                  setPage(0)
                }}
                label="Customer"
              />
            </Box>
            <TextField
              select
              label="Branch"
              value={outletId}
              onChange={(event) => {
                setOutletId(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All branches</MenuItem>
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Mode"
              value={mode}
              onChange={(event) => {
                setMode(event.target.value)
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All modes</MenuItem>
              {MODES.map((value) => (
                <MenuItem key={value} value={value}>
                  {humanise(value)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* What is owed, before what came in. Shown without being asked for:
          somebody standing at this screen with a customer in front of them is
          about to take money, and the bills are the reason. */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Outstanding {customer ? `— ${customer.name}` : outletId ? '— this branch' : ''}
                </Typography>
                <Typography variant="h6" color={owedTotal > 0 ? 'warning.main' : 'text.primary'}>
                  {formatMoney(owedTotal)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {bills.length} {bills.length === 1 ? 'bill' : 'bills'} unpaid
              </Typography>
            </Stack>
            {canWrite('payment') && customer && bills.length > 0 ? (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCollecting(true)}>
                Collect against these bills
              </Button>
            ) : null}
          </Stack>

          {/* Named one by one only for a chosen customer: the whole company's
              unpaid bills are the ageing report, not a payments screen. */}
          {customer && bills.length > 0 ? (
            <Table size="small" sx={{ mt: 1.5 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell align="right">Bill amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.slice(0, 12).map((bill) => (
                  <TableRow key={bill.id} hover>
                    <TableCell>{bill.invoiceNumber}</TableCell>
                    <TableCell>{formatDate(bill.invoiceDate)}</TableCell>
                    <TableCell>{bill.orderNumber ?? '—'}</TableCell>
                    <TableCell align="right">{formatMoney(bill.totalAmount)}</TableCell>
                    <TableCell align="right">{formatMoney(bill.paidAmount)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="warning.main">
                        {formatMoney(bill.balanceAmount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
          {customer && bills.length > 12 ? (
            <Typography variant="caption" color="text.secondary">
              Showing the 12 oldest of {bills.length}.
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.content.length === 0 ? (
          <EmptyState message="No receipts in this window." />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Unallocated</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.content.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>{payment.receiptNumber}</TableCell>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>{payment.customerName}</TableCell>
                    <TableCell>{payment.orderNumber ?? '—'}</TableCell>
                    <TableCell>{humanise(payment.paymentMode)}</TableCell>
                    <TableCell align="right">{formatMoney(payment.amount, true)}</TableCell>
                    <TableCell align="right">
                      {payment.unallocatedAmount > 0 ? (
                        <Typography variant="body2" color="warning.main">
                          {formatMoney(payment.unallocatedAmount)}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={payment.reversed ? 'REVERSED' : payment.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {can('payment.edit') && !payment.reversed && (
                          <Tooltip title="Correct the details of this receipt">
                            <IconButton size="small" onClick={() => setEditing(payment)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {can('payment.reverse') && !payment.reversed && (
                          <Button size="small" startIcon={<UndoIcon />} onClick={() => setReversing(payment)}>
                            Reverse
                          </Button>
                        )}
                        <DocumentActions
                          path={`/api/v1/payments/${payment.id}/pdf`}
                          fileName={`${payment.receiptNumber.replace(/\//g, '-')}.pdf`}
                          label="receipt"
                        />
                        {/* The receipt is the customer's proof of paying, so it
                            can be sent as well as printed. */}
                        <SendDocumentIcon kind="receipt" id={payment.id} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && (
              <TablePagination
                component="div"
                count={data.totalElements}
                page={data.page}
                onPageChange={(_event, newPage) => setPage(newPage)}
                rowsPerPage={size}
                onRowsPerPageChange={(event) => {
                  setSize(Number(event.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            )}
          </>
        )}
      </Card>

      </>
      )}

      <RecordPaymentDialog open={entryOpen} onClose={() => setEntryOpen(false)} />
      {/* Opened from the outstanding panel, already on that customer, with
          their bills listed and ready to be ticked. */}
      <RecordPaymentDialog
        open={collecting}
        customerId={customer?.id}
        onClose={() => setCollecting(false)}
      />
      <RecordPaymentDialog
        open={Boolean(editing)}
        payment={editing}
        onClose={() => setEditing(null)}
      />
      <ReversalDialog payment={reversing} onClose={() => setReversing(null)} />
    </>
  )
}

function ReversalDialog({ payment, onClose }: { payment: Payment | null; onClose: () => void }) {
  const reverse = useReversePayment()
  const [reason, setReason] = useState('')

  return (
    <Dialog open={Boolean(payment)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reverse {payment?.receiptNumber}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={reverse.error} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          Payments are never deleted. Reversal unwinds every allocation, restores the customer balance
          and writes an audit entry against your name.
        </Alert>
        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          multiline
          minRows={3}
          fullWidth
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Keep it</Button>
        <Button
          color="error"
          variant="contained"
          disabled={reverse.isPending || !reason.trim() || !payment}
          onClick={async () => {
            if (!payment) return
            await reverse.mutateAsync({ id: payment.id, reason })
            onClose()
          }}
        >
          Reverse
        </Button>
      </DialogActions>
    </Dialog>
  )
}
